import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Tooltip,
  message,
  Button,
  Row,
  Col,
  Divider,
  Space,
  Typography,
} from "antd";
import { QuestionCircleOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/zh_TW";
import SCMContractApi, { CreateContractPayload, UpdateContractPayload, VendorDetails } from "@api/scmAPI";
import useMemberInfo from "@hooks/useMemberInfo";
import InfiniteScrollSelect from "@component/Common/InfiniteScrollSelect";
import { useAppDispatch } from "src/store";
import { fetchProductCategoryList } from "@redux/productSlice";
import { useSelector } from "react-redux";
import { RootState } from "@redux/rootReducer";
import { RequiredMark } from "src/styles/common";
import timeUtils from "@utils/timeUtils";
import {
  CONTRACT_MODE,
  CONTRACT_STATUS,
  ContractFieldType1,
  ContractFieldType2,
  contractModeOptions,
  getBackChargeText,
} from "./utils";

const StyledLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ModalContent = styled.div`
  padding: 10px 0 20px 0;
`;

const ReadOnlyText = styled.div`
  padding: 4px 11px;
  color: #00000073;
`;
const SpaceFormItem = styled(Form.Item)`
  .ant-form-item-no-colon {
    margin-left: 12px;
  }
`;

const FormItemCol = styled(Col)`
  .ant-form-item-label {
    padding: 0;
  }
`;
const CategoryRateWrapper = styled.div`
  background: #f0f7ff;
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

type EditMetaType = {
  vendorId: number;
  vendorName: string;
  ubn: string;
  lastUpdatedBy: string | null;
  status: number;
};
interface CreateContractModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  mode?: "create" | "edit" | "copy";
  contractId?: number;
}

export default function CreateContractModal(props: CreateContractModalProps) {
  const { visible, onCancel, onSuccess, mode = "create", contractId } = props;
  const dispatch = useAppDispatch();
  const { productCategory } = useSelector((state: RootState) => state.product);
  const [form] = Form.useForm();
  const { userName } = useMemberInfo();
  const [loading, setLoading] = useState(false);
  const [editMeta, setEditMeta] = useState<EditMetaType | null>(null); // 編輯/複製模式：儲存詳情資訊
  const [allVendors, setAllVendors] = useState<VendorDetails[]>([]); // 儲存所有已加載的廠商資料，用於查找完整的廠商資訊

  const isEditMode = mode === "edit";
  const isCopyMode = mode === "copy";

  const isActiveContract = isEditMode && editMeta?.status === CONTRACT_STATUS.ACTIVE;
  const contractMode = Form.useWatch("contractMode", form);

  // 取得廠商列表的 API 函數（供 InfiniteScrollSelect 使用）
  const fetchVendorOptions = useCallback(async (params: { offset: number; limit: number; name?: string }) => {
    const { offset, name } = params;
    try {
      const response = await SCMContractApi.fetchSCMVendorList(params);
      if (name) {
        // 搜尋
        setAllVendors(response.results);
      } else if (offset === 0) {
        // 初始載入，重置列表
        setAllVendors(response.results);
      } else {
        // 非搜尋模式下，追加新資料
        setAllVendors((prev) => {
          const newList = prev.slice();
          response.results.forEach((item: any) => newList.push(item));
          return newList;
        });
      }
      return response;
    } catch (error) {
      return { count: 0, next: null, previous: null, results: [] };
    }
  }, []);
  // 處理合作類型改變
  const handleContractModeChange = (value: number) => {
    form.setFieldsValue({
      depositAmount: undefined,
      minOrderQty: undefined,
      acceptableDays: undefined,
      returnableDays: undefined,
      arrivalDays: undefined,
    });
  };
  // 處理表單提交
  const handleSubmit = async (values: any, force: boolean = false) => {
    try {
      setLoading(true);
      if (isEditMode && contractId) {
        // 編輯模式
        const payload: UpdateContractPayload = {
          contractMode: values.contractMode,
          startDate: values.startDate.format("YYYY-MM-DD"),
          endDate: values.endDate.format("YYYY-MM-DD"),
          depositAmount: values.depositAmount,
          minOrderQty: values.minOrderQty,
          acceptableDays: values.acceptableDays,
          returnableDays: values.returnableDays,
          arrivalDays: values.arrivalDays,
        };
        // 僅寄倉/轉單需要帶品類抽成

        if (values.categoryRates && values.categoryRates.length > 0) {
          payload.categoryRates = values.categoryRates.map((item: any) => ({
            // 既有項目保留 id，新項目不帶 id
            ...(item.id !== undefined && item.id !== null ? { id: item.id } : {}),
            categoryStId: item.categoryStId,
            categoryNdId: item.categoryNdId || null,
            commissionRate: Number(item.commissionRate),
          }));
        }
        await SCMContractApi.updateSCMContract(contractId, payload);
        message.success("儲存成功");
      } else {
        // 新增/複製模式
        const payload: CreateContractPayload = {
          vendorId: isCopyMode ? editMeta?.vendorId : values.vendorId,
          contractMode: values.contractMode,
          startDate: values.startDate.format("YYYY-MM-DD"),
          endDate: values.endDate.format("YYYY-MM-DD"),
          depositAmount: values.depositAmount,
          minOrderQty: values.minOrderQty,
          acceptableDays: values.acceptableDays,
          returnableDays: values.returnableDays,
          arrivalDays: values.arrivalDays,
          force,
        };
        // 僅寄倉/轉單需要帶品類抽成
        if (values.categoryRates && values.categoryRates.length > 0) {
          payload.categoryRates = values.categoryRates.map((item: any) => ({
            id: null,
            categoryStId: item.categoryStId,
            categoryNdId: item.categoryNdId || null,
            commissionRate: Number(item.commissionRate),
          }));
        }
        await SCMContractApi.createSCMContract(payload);
        message.success("建立成功");
      }
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error?.statusCode === 2006) {
        Modal.confirm({
          title: "本次建立的新合約僅涵蓋部分品類",
          content: (
            <>
              <Typography.Paragraph>目前新合約未包含先前合約中的全部品類：</Typography.Paragraph>
              <Typography.Paragraph strong>{error?.message}</Typography.Paragraph>
              <Typography.Paragraph>
                若有上架中商品屬於未涵蓋的品類，系統將於新合約生效時自動下架相關商品；
              </Typography.Paragraph>
              <Typography.Paragraph>請確認是否需補充合約品類，以避免商品無法正常販售</Typography.Paragraph>
            </>
          ),
          okText: "確認",
          cancelText: "取消",
          onOk: () => {
            handleSubmit(values, true);
          },
          width: 500,
        });
      }
    } finally {
      setLoading(false);
    }
  };
  // 處理取消
  const handleCancel = () => {
    form.resetFields();
    setAllVendors([]);
    onCancel();
  };
  // -------------- UI Functions--------------
  // 生成欄位說明圖示
  const getFieldHelpIcon = (label: string, title: string) => {
    return (
      <StyledLabel>
        {label}
        <Tooltip title={title}>
          <QuestionCircleOutlined style={{ color: "#8c8c8c", cursor: "help" }} />
        </Tooltip>
      </StyledLabel>
    );
  };

  // 根據合作類型生成必填欄位
  const getRequiredFields = useCallback((contractModeValue: number | undefined): {
    name: string;
    required: boolean;
    label: string;
    help: string;
  }[] => {
    // fieldType1:買斷/買斷預付100% fieldType2:寄倉/轉單
    const modeNum = Number(contractModeValue);
    return modeNum === CONTRACT_MODE.CONSIGNMENT || modeNum === CONTRACT_MODE.TRANS_SHIPMENT
      ? ContractFieldType2
      : ContractFieldType1;
  }, []);

  // 新增品類抽成行
  const handleAddCategoryRateRow = async () => {
    try {
      const current = form.getFieldValue("categoryRates") || [];
      // 驗證最後一列的必填
      const lastIndex = current.length - 1;
      if (lastIndex >= 0) {
        await form.validateFields([
          ["categoryRates", lastIndex, "categoryStId"],
          ["categoryRates", lastIndex, "commissionRate"],
        ]);
      }
      const next = current.concat({
        categoryStId: undefined,
        categoryNdId: undefined,
        commissionRate: undefined,
      });
      form.setFieldsValue({ categoryRates: next });
    } catch (e) {
      message.error("請先完成當前列必填欄位");
    }
  };

  // 取得品類列表
  useEffect(() => {
    if (visible) {
      dispatch(fetchProductCategoryList());
    }
  }, [dispatch, visible]);
  // 編輯/複製模式：載入詳情並回填
  useEffect(() => {
    const fetchDetail = async (id: number) => {
      try {
        setLoading(true);
        const detail = await SCMContractApi.fetchContractDetail(id);
        setEditMeta({
          vendorId: detail.vendorId,
          vendorName: detail.name,
          ubn: detail.ubn,
          lastUpdatedBy: detail.lastUpdatedBy,
          status: detail.status,
        });

        if (!detail.contractMode) return;

        const categoryRates = Array.isArray(detail.categoryRates)
          ? detail.categoryRates.map((it: any) => ({
              id: it.id ?? null,
              categoryStId: it.categoryStId,
              categoryNdId: it.categoryNdId ?? undefined,
              commissionRate: it.commissionRate,
            }))
          : [{}];

        form.setFieldsValue({
          // vendor 在編輯/複製模式僅顯示，不回填到可選欄位
          contractMode: detail.contractMode,
          startDate: detail.startDate ? timeUtils.parseToMoment(detail.startDate) : undefined,
          endDate: detail.endDate ? timeUtils.parseToMoment(detail.endDate) : undefined,
          depositAmount: detail.depositAmount,
          minOrderQty: detail.minOrderQty,
          acceptableDays: detail.acceptableDays,
          returnableDays: detail.returnableDays,
          arrivalDays: detail.arrivalDays,
          categoryRates,
        });
      } catch (e: any) {
        message.error(`取得合約詳情失敗:${e.message}`);
      } finally {
        setLoading(false);
      }
    };
    if (contractId && (isCopyMode || isEditMode)) {
      fetchDetail(contractId);
    }
    // 編輯/複製 modal 關閉時清空
    if (!visible) {
      setEditMeta(null);
      form.resetFields();
    }
  }, [visible, isCopyMode, isEditMode, contractId, dispatch, form]);

  return (
    <Modal open={visible} onCancel={handleCancel} width={700} footer={null}>
      <ModalContent>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 5 }}
          labelAlign="left"
          colon={false}
          onFinish={handleSubmit}
          initialValues={{
            categoryRates: [{}],
          }}
        >
          <SpaceFormItem label="最後更新者">
            <ReadOnlyText>{editMeta?.lastUpdatedBy || userName}</ReadOnlyText>
          </SpaceFormItem>
          {isEditMode || isCopyMode ? (
            <>
              <SpaceFormItem label="廠商名稱">
                <ReadOnlyText>{editMeta?.vendorName || "-"}</ReadOnlyText>
              </SpaceFormItem>
              <SpaceFormItem label="統編">
                <ReadOnlyText>{editMeta?.ubn || "-"}</ReadOnlyText>
              </SpaceFormItem>
            </>
          ) : (
            <>
              <Form.Item label="廠商名稱" name="vendorId" rules={[{ required: true, message: "請選擇廠商名稱" }]}>
                <InfiniteScrollSelect
                  placeholder="請選擇廠商名稱"
                  config={{ offset: 0, limit: 20 }}
                  fetchOptions={fetchVendorOptions}
                  style={{ width: "300px" }}
                />
              </Form.Item>
              <SpaceFormItem label="統編" shouldUpdate={(prev: any, cur: any) => prev.vendorId !== cur.vendorId}>
                {({ getFieldValue }) => {
                  const vendor = allVendors.find((v) => v.id === getFieldValue("vendorId"));
                  return <ReadOnlyText>{vendor?.ubn || "選擇廠商後自動帶入"}</ReadOnlyText>;
                }}
              </SpaceFormItem>
            </>
          )}
          <Form.Item label="合作類型" name="contractMode" rules={[{ required: true, message: "請選擇合作類型" }]}>
            <Select
              placeholder="請選擇合作類型"
              onChange={handleContractModeChange}
              options={contractModeOptions}
              style={{ width: "300px" }}
              disabled={isActiveContract}
            />
          </Form.Item>
          <Form.Item label="生效日" name="startDate" rules={[{ required: true, message: "請選擇生效日" }]}>
            <DatePicker
              locale={locale}
              style={{ width: "300px" }}
              format="YYYY-MM-DD"
              disabledDate={(current) => timeUtils.getDisabledDate(current, { disableBase: true })}
              placeholder="請選擇生效日"
              disabled={isActiveContract}
              onChange={() => form.setFieldsValue({ endDate: undefined })}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev: any, cur: any) => prev.startDate !== cur.startDate}>
            {({ getFieldValue }) => {
              const startDate = getFieldValue("startDate");
              return (
                <Form.Item label="結束日" name="endDate" rules={[{ required: true, message: "請選擇結束日" }]}>
                  <DatePicker
                    locale={locale}
                    style={{ width: "300px" }}
                    format="YYYY-MM-DD"
                    disabledDate={(current) =>
                      timeUtils.getDisabledDate(current, { disableBase: true, baseDate: startDate })
                    }
                    placeholder="請選擇結束日"
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <SpaceFormItem label="後扣費用">
            <ReadOnlyText>{getBackChargeText(contractMode)}</ReadOnlyText>
          </SpaceFormItem>
          <Divider />
          <Row gutter={16}>
            <Form.Item noStyle shouldUpdate={(prev: any, cur: any) => prev.contractMode !== cur.contractMode}>
              {() => {
                const requiredFields = getRequiredFields(contractMode);
                return requiredFields.map((field) => (
                  <FormItemCol span={8}>
                    <Form.Item
                      label={getFieldHelpIcon(field.label, field.help)}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      name={field.name}
                      rules={[{ required: field.required, message: `請輸入${field.label}` }]}
                      required={field.required}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder={`請輸入${field.label}`}
                        min={0}
                        precision={0}
                      />
                    </Form.Item>
                  </FormItemCol>
                ));
              }}
            </Form.Item>
          </Row>
          {/* 品類抽成(%) 僅寄倉/轉單顯示 */}
          <Form.Item noStyle shouldUpdate={(prev: any, cur: any) => prev.contractMode !== cur.contractMode}>
            {({ getFieldValue, setFieldsValue }) => {
              const modeNum = Number(getFieldValue("contractMode") || -999);
              const showCategoryRate =
                modeNum === CONTRACT_MODE.CONSIGNMENT || modeNum === CONTRACT_MODE.TRANS_SHIPMENT;
              if (!showCategoryRate) return null;
              const level1Options = Object.values(productCategory.level1).map((category: any) => ({
                label: category.name,
                value: category.id,
              }));
              return (
                <CategoryRateWrapper>
                  <Row gutter={16} style={{ marginBottom: 8 }}>
                    <FormItemCol span={8}>
                      <RequiredMark>*</RequiredMark>
                      第一品類
                    </FormItemCol>
                    <FormItemCol span={8}>第二品類</FormItemCol>
                    <FormItemCol span={8}>
                      <RequiredMark>*</RequiredMark> 抽成%
                    </FormItemCol>
                  </Row>
                  <Form.List name="categoryRates">
                    {(fields, { remove }) => {
                      return (
                        <>
                          {fields.map(({ key, name }) => {
                            const firstSelected: number | undefined = form.getFieldValue([
                              "categoryRates",
                              name,
                              "categoryStId",
                            ]);
                            const level2Options = firstSelected
                              ? (productCategory.level1[firstSelected]?.children || []).map((c: any) => ({
                                  label: c.name,
                                  value: c.id,
                                }))
                              : [];
                            return (
                              <Row gutter={16} key={key} style={{ marginBottom: 12 }}>
                                <FormItemCol span={8}>
                                  <Form.Item
                                    name={[name, "categoryStId"]}
                                    rules={[{ required: true, message: "請選擇第一品類" }]}
                                  >
                                    <Select
                                      placeholder="請選擇"
                                      options={level1Options}
                                      onChange={(value: number) => {
                                        // 重置第二品類
                                        const current = form.getFieldValue("categoryRates") || [];
                                        // 從 productCategory 取得第一品類的抽成%
                                        const rate = productCategory.level1[value]?.commissionRate;
                                        current[name] = {
                                          ...current[name],
                                          categoryStId: value,
                                          categoryNdId: undefined,
                                          commissionRate: rate,
                                        };
                                        setFieldsValue({ categoryRates: current });
                                      }}
                                      disabled={isActiveContract}
                                    />
                                  </Form.Item>
                                </FormItemCol>
                                <FormItemCol span={8}>
                                  <Form.Item name={[name, "categoryNdId"]}>
                                    <Select
                                      placeholder={isActiveContract ? "-" : "請選擇"}
                                      disabled={!firstSelected || isActiveContract}
                                      options={level2Options}
                                      onChange={(value: number | undefined) => {
                                        // 從 productCategory 取得抽成%
                                        const current = form.getFieldValue("categoryRates") || [];
                                        const rate = value
                                          ? productCategory.level2[value]?.commissionRate
                                          : productCategory.level1[current[name]?.categoryStId]?.commissionRate;
                                        current[name] = {
                                          ...current[name],
                                          categoryNdId: value,
                                          commissionRate: rate,
                                        };
                                        setFieldsValue({ categoryRates: current });
                                      }}
                                      allowClear
                                    />
                                  </Form.Item>
                                </FormItemCol>
                                <FormItemCol span={7}>
                                  <Form.Item
                                    name={[name, "commissionRate"]}
                                    rules={[{ required: true, message: "請輸入抽成%" }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      precision={0}
                                      step={1}
                                      style={{ width: "100%" }}
                                      placeholder="請輸入"
                                      disabled={isActiveContract}
                                    />
                                  </Form.Item>
                                </FormItemCol>
                                <FormItemCol span={1} style={{ padding: 0 }}>
                                  {fields.length >= 2 && name !== 0 && !isActiveContract && (
                                    <Button type="link" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                  )}
                                </FormItemCol>
                              </Row>
                            );
                          })}
                        </>
                      );
                    }}
                  </Form.List>
                  <Button
                    type="link"
                    onClick={handleAddCategoryRateRow}
                    style={{ paddingLeft: 0 }}
                    disabled={isActiveContract}
                  >
                    <PlusOutlined /> 新增
                  </Button>
                </CategoryRateWrapper>
              );
            }}
          </Form.Item>
          <Row justify="end">
            <Space>
              <Button onClick={handleCancel} disabled={loading}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                確定
              </Button>
            </Space>
          </Row>
        </Form>
      </ModalContent>
    </Modal>
  );
}
